import {
    ApiAction,
    ApiFragment,
    BrowseStatus,
    ContextMetadata,
    DeleteStatus,
    Family,
    FragmentId,
    MediaObjectId,
    MediaType,
    Metadata,
    PartialMetadata,
    RecordId,
    RecordPhase,
    RecordStatus,
    RecordType,
    RecordTypeStructure,
    Task,
    Zone,
    get,
    set
} from '@stylehaven/components';
import { set as _set } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';

export class RecordBuilder {
    private metadata: Metadata;

    constructor(metadata: Metadata, id?: string) {
        if (metadata) {
            this.metadata = cloneDeep(metadata);
        }

        if (!get(this.metadata, 'Structural', 'RecordStructure')) {
            set(this.metadata, RecordTypeStructure.DataFlat, 'Structural', 'RecordStructure');
        }

        id ? this.setIds(id) : this.setRandomizedIds();
        this.clearRelations();
    }

    public static EmptyRecord(id?: string): ApiFragment {
        return new RecordBuilder({} as Metadata, id).build();
    }

    private setRandomizedIds(): void {
        const id: string = generateFragmentId();

        set(this.metadata, id as FragmentId, 'Internal', 'FragmentId');
        set(this.metadata, id as MediaObjectId, 'Internal', 'MediaObjectId');
        set(this.metadata, id as RecordId, 'Internal', 'RecordId');
    }

    private setIds(id: string): void {
        set(this.metadata, id as FragmentId, 'Internal', 'FragmentId');
        set(this.metadata, id as MediaObjectId, 'Internal', 'MediaObjectId');
        set(this.metadata, id as RecordId, 'Internal', 'RecordId');
    }

    private clearRelations(): void {
        set(this.metadata, {}, 'Structural', 'Relations');
    }

    withRecordStatus(recordStatus: RecordStatus): RecordBuilder {
        set(this.metadata, recordStatus, 'Administrative', 'RecordStatus');
        return this;
    }

    withRecordPhase(recordPhase: RecordPhase): RecordBuilder {
        set(this.metadata, recordPhase, 'RightsManagement', 'RecordPhase');
        return this;
    }

    withDeleteStatus(deleteStatus: DeleteStatus): RecordBuilder {
        set(this.metadata, deleteStatus, 'Administrative', 'DeleteStatus');
        return this;
    }

    withRecordType(recordType: RecordType): RecordBuilder {
        set(this.metadata, recordType, 'Administrative', 'RecordType');
        return this;
    }

    withRecordTypeStructure(recordStructure: RecordTypeStructure): RecordBuilder {
        set(this.metadata, recordStructure, 'Structural', 'RecordStructure');
        return this;
    }

    withMediaType(type: MediaType): RecordBuilder {
        set(this.metadata, type, 'Administrative', 'Type');
        return this;
    }

    withClassification(classificationId: MediaObjectId): RecordBuilder {
        set(this.metadata, [classificationId], Family.Structural, 'ReferenceCodes', RecordType.Classification);
        return this;
    }

    withContext(context: keyof ContextMetadata, value: boolean): RecordBuilder {
        set(this.metadata, value, 'Context', context);
        return this;
    }

    withChildren(value: boolean): RecordBuilder {
        set(this.metadata, value, 'Structural', 'HasChildren');
        return this;
    }

    withNonRepresentationChildren(value: boolean): RecordBuilder {
        set(this.metadata, value, 'Structural', 'HasNonRepresentationChildren');
        return this;
    }

    withMainFragment(value: FragmentId): RecordBuilder {
        set(this.metadata, value, 'Structural', 'MainFragment');
        return this;
    }

    inSelection(selectionId: FragmentId): RecordBuilder {
        return this.addRelations('ContainedBy', [selectionId]);
    }

    inCollection(collectionId: MediaObjectId): RecordBuilder {
        set(this.metadata, [collectionId], Family.Structural, 'ReferenceCodes', RecordType.Mh2Collection);
        set(this.metadata, collectionId as unknown as RecordId, Family.Structural, 'ParentRecordId');
        return this;
    }

    withSelectionChildren(...fragmentId: FragmentId[]): RecordBuilder {
        return this.addRelations('Contains', fragmentId);
    }

    withRejectionError(motivation: string, date?: string, user?: string, reason?: string): RecordBuilder {
        if (!this.metadata.Administrative.RecordRejections?.Rejection) {
            _set(this.metadata, 'Administrative.RecordRejections.Rejection', []);
        }

        this.metadata.Administrative.RecordRejections!.Rejection.push({
            Motivation: motivation,
            Description: motivation,
            Descriptions: [],
            Date: date ?? new Date().toISOString(),
            User: user ?? 'User',
            Reason: reason ?? 'ERROR'
        });
        return this;
    }

    candidateParentOf(...ids: FragmentId[]): RecordBuilder {
        return this.addRelations('IsCandidateParentOf', ids);
    }

    candidateChildOf(...ids: FragmentId[]): RecordBuilder {
        return this.addRelations('IsCandidateChildOf', ids);
    }

    withZone(zone: Zone): RecordBuilder {
        set(this.metadata, zone, Family.RightsManagement, 'Zone');
        return this;
    }

    withTitle(title: string): RecordBuilder {
        set(this.metadata, title, Family.Descriptive, 'Title');
        return this;
    }

    withProperty<F extends keyof PartialMetadata, P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>>(
        family: F,
        property: P,
        value: any
    ): RecordBuilder {
        set(this.metadata, value, family, property);
        return this;
    }

    private addRelations(relationType: string, ids: FragmentId[]): RecordBuilder {
        const relations: FragmentId[] = get(this.metadata, 'Structural', 'Relations', relationType) || [];
        const newRelations: FragmentId[] = Array.from(new Set([...relations, ...ids])) as FragmentId[];

        set(this.metadata, newRelations, 'Structural', 'Relations', relationType);
        return this;
    }

    setBrowseStatus(status: BrowseStatus): RecordBuilder {
        set(this.metadata, status, Family.Internal, 'BrowseStatus');
        return this;
    }

    setPreviewRecordId(recordId: RecordId): RecordBuilder {
        set(this.metadata, recordId, Family.Structural, 'PreviewRecordId');
        return this;
    }

    setFailedJobs(value: boolean): RecordBuilder {
        set(this.metadata, value, Family.Administrative, 'HasFailedJobs');
        return this;
    }

    setKeywords(values: string[], AI: boolean = false): RecordBuilder {
        if (AI) {
            set(this.metadata, values, Family.Dynamic, 'Mh2AiKeywords', 'Mh2AiKeyword');
            return this;
        }
        set(this.metadata, values, Family.Descriptive, 'Keywords', 'Keyword');
        return this;
    }

    addTask(task: Task): RecordBuilder {
        if (!this.metadata.Workflow?.Tasks?.Task) {
            this.metadata.Workflow = {
                Tasks: {
                    Task: []
                }
            };
        }

        this.metadata.Workflow.Tasks.Task.push(task);
        return this;
    }

    addApiAction(action: ApiAction): RecordBuilder {
        if (!this.metadata._links) {
            this.metadata._links = [];
        }

        this.metadata._links.push(action);
        return this;
    }

    build(): ApiFragment {
        return new ApiFragment(cloneDeep(this.metadata));
    }
}

export function generateFragmentId(): FragmentId {
    return (Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)) as FragmentId;
}
